import { useMemo, useState } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";
import { formatCanonical } from "../../exchange/market-format.ts";
import { Icon } from "../../components/icons/Icon.tsx";
import "../../styles/powerball-workspace.css";
import "../../styles/powerball-predictions.css";
import "./form-elite.css";

interface Pick {
  fixture_id: string; club: string; classification: string;
  home_team: string; away_team: string; league: string; kickoff_at: string;
  status: "PLAY" | "DOWNGRADE"; grade: "A" | "B";
  market: string; odds: number; bookmaker: string | null;
  price_at: string; outcome: string | null;
}
interface Prediction {
  fixture_id: string; club: string; classification: string;
  home_team: string; away_team: string; league: string; kickoff_at: string;
  market: string; odds: number | null; bookmaker: string | null;
  price_at: string | null; predicted_at: string; prediction_status: "MODEL_FORECAST";
}
export interface FormEliteBoardData {
  active_clubs: number; mapped_clubs: number; updated_at: string | null;
  predictions: Prediction[]; selections: Pick[];
}
const clock = new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Accra", hour: "2-digit", minute: "2-digit", hour12: false });
const day = new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Accra", weekday: "long", day: "numeric", month: "short" });
const dateKey = new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Accra", year: "numeric", month: "2-digit", day: "2-digit" });

function displayMarket(pick: Pick | Prediction): { family: string; selection: string } {
  const team = pick.market.includes("_home_") || pick.market.endsWith("_home")
    ? pick.home_team : pick.away_team;
  const line = pick.market.match(/over_(\d+)_(\d+)$/);
  const goalLine = line ? `${line[1]}.${line[2]}` : "";
  if (pick.market.startsWith("team_goals_"))
    return { family: "Team goals", selection: `${team} over ${goalLine} goals` };
  if (pick.market.startsWith("team_to_score_"))
    return { family: "Team to score", selection: `${team} to score` };
  if (pick.market.startsWith("first_half_"))
    return { family: "First-half goals", selection: `Over ${goalLine} goals` };
  if (pick.market.startsWith("second_half_"))
    return { family: "Second-half goals", selection: `Over ${goalLine} goals` };
  if (pick.market.startsWith("double_chance_"))
    return { family: "Double chance", selection: `${team} or draw` };
  if (pick.market.startsWith("draw_no_bet_"))
    return { family: "Draw no bet", selection: `${team} draw no bet` };
  if (pick.market.startsWith("match_winner_"))
    return { family: "Match result", selection: `${team} to win` };
  if (pick.market.startsWith("total_goals_"))
    return { family: "Total goals", selection: formatCanonical(pick.market) };
  return { family: "Both teams to score", selection: formatCanonical(pick.market) };
}

export function FormEliteBoard({ data, onRefresh, refreshing = false }: {
  data: FormEliteBoardData; onRefresh?: () => void; refreshing?: boolean;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [league, setLeague] = useState("all");
  const [grade, setGrade] = useState("all");
  const [view, setView] = useState<"predictions" | "reviewed">("predictions");
  const rows = useMemo(() => [...(view === "predictions" ? data.predictions : data.selections)]
    .sort((a, b) => Date.parse(a.kickoff_at) - Date.parse(b.kickoff_at)),
    [data.predictions, data.selections, view]);
  const leagues = [...new Set(rows.map((row) => row.league))].sort();
  const visible = rows.filter((row) => (league === "all" || row.league === league)
    && (view === "predictions" || grade === "all" || ("grade" in row && row.grade === grade))
    && `${row.club} ${row.home_team} ${row.away_team} ${row.league} ${row.market}`
      .toLowerCase().includes(query.trim().toLowerCase()));
  const groups = new Map<string, (Pick | Prediction)[]>();
  for (const row of visible) {
    const key = dateKey.format(new Date(row.kickoff_at));
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return <main className="fp pb-workspace form-elite">
    <nav className="pb-workspace-nav" aria-label="FORM ELITE workspace">
      <span className="form-elite-location">FOOTBALL / FORM ELITE</span>
    </nav>
    <header className="pb-workspace-header">
      <div><span className="pb-eyebrow">CLUB INTELLIGENCE</span><h1>FORM ELITE</h1>
        <p>Current predictions from the monitored club board.</p></div>
      <button type="button" className="pb-secondary-button" onClick={onRefresh}
        disabled={!onRefresh || refreshing} title="Refresh selections">
        <Icon name="refresh" size={16} /> Refresh
      </button>
    </header>
    <div className="form-elite-summary" aria-label="Board summary">
      <div><span>Monitored clubs</span><strong>{data.active_clubs}</strong></div>
      <div><span>Mapped clubs</span><strong>{data.mapped_clubs}</strong></div>
      <div><span>Current predictions</span><strong>{data.predictions.length}</strong></div>
      <div><span>Competitions</span><strong>{leagues.length}</strong></div>
    </div>
    <section className="pb-board" aria-label="FORM ELITE selections">
      <header className="pb-board-header"><div><span className="pb-board-eyebrow">CURRENT BOARD</span>
        <h3>{view === "predictions" ? "Predictions" : "Reviewed selections"} <span>{rows.length}</span></h3>
        <p>Kickoff times in Accra · {view === "predictions" ? "Model forecast, not a reviewed ticket" : "Decimal odds"}</p></div>
        <span className="pb-board-updated">{data.updated_at ? `Checked ${clock.format(new Date(data.updated_at))}` : "Awaiting first scan"}</span>
      </header>
      <div className="form-elite-view-tabs" role="tablist" aria-label="Board view">
        <button type="button" role="tab" aria-selected={view === "predictions"}
          className={view === "predictions" ? "active" : ""}
          onClick={() => { setView("predictions"); setLeague("all"); }}>Predictions ({data.predictions.length})</button>
        <button type="button" role="tab" aria-selected={view === "reviewed"}
          className={view === "reviewed" ? "active" : ""}
          onClick={() => { setView("reviewed"); setLeague("all"); }}>Reviewed ({data.selections.length})</button>
      </div>
      <div className="pb-board-toolbar form-elite-toolbar">
        <label className="pb-board-search"><Icon name="search" size={16} />
          <input type="search" aria-label="Search FORM ELITE selections" placeholder="Team, fixture or market" value={query}
            onChange={(event) => setQuery(event.target.value)} /></label>
        <label>Competition<select aria-label="Filter by competition" value={league}
          onChange={(event) => setLeague(event.target.value)}><option value="all">All</option>
          {leagues.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        {view === "reviewed" && <label>Grade<select aria-label="Filter by grade" value={grade}
          onChange={(event) => setGrade(event.target.value)}><option value="all">All</option>
          <option value="A">A</option><option value="B">B</option></select></label>}
      </div>
      {rows.length === 0 ? <div className="pb-board-empty"><h4>No current {view === "predictions" ? "predictions" : "reviewed selections"}</h4>
        <p>{view === "predictions" ? "No current fixtures have enough model evidence yet." : "The final ticket review is still pending."}</p></div>
        : visible.length === 0 ? <div className="pb-board-empty"><h4>No matching selections</h4>
          <p>Try another club, competition or grade.</p></div>
          : <div className="pb-board-groups">{[...groups].map(([key, picks]) =>
            <section key={key} className="pb-board-day" aria-label={day.format(new Date(picks[0]!.kickoff_at))}>
              <div className="pb-board-day-heading"><h4>{day.format(new Date(picks[0]!.kickoff_at))}</h4>
                <span>{picks.length} {picks.length === 1 ? "selection" : "selections"}</span></div>
              <div className="pb-board-columns" aria-hidden="true"><span>#</span><span>Kickoff</span>
                <span>Fixture / competition</span><span>Market / selection</span><span>Odds</span></div>
              <ol className="pb-board-list">{picks.map((pick, index) =>
                <li key={`${pick.fixture_id}:${pick.club}`} className="pb-board-row">
                  <span className="pb-board-number">{String(index + 1).padStart(2, "0")}</span>
                  <time className="pb-board-time" dateTime={pick.kickoff_at}>{clock.format(new Date(pick.kickoff_at))}</time>
                  <div className="pb-board-fixture"><strong>{pick.home_team}<span className="pb-board-versus">vs</span>{pick.away_team}</strong>
                    <span className="pb-board-league">{pick.league} · {pick.club} · {"grade" in pick ? `Grade ${pick.grade}` : "Prediction"}</span></div>
                  <div className="pb-board-selection"><span>{displayMarket(pick).family}</span>
                    <strong>{displayMarket(pick).selection}</strong>
                    <span className="form-elite-bookmaker">{pick.price_at && pick.bookmaker
                      ? `${pick.bookmaker} · ${clock.format(new Date(pick.price_at))}`
                      : "Odds unavailable"}</span></div>
                  <span className="pb-board-price">{pick.odds == null ? "N/A" : Number(pick.odds).toFixed(2)}</span>
                </li>)}</ol>
            </section>)}</div>}
      <footer className="pb-board-footer"><span>{visible.length} of {rows.length} {view === "predictions" ? "predictions" : "selections"}</span>
        <span>{view === "predictions" ? "Predictions are not booking-ready" : "Prices can change before kickoff"}</span></footer>
    </section>
  </main>;
}

export function FormElitePage(): JSX.Element {
  const board = useRpcQuery<FormEliteBoardData>("get_form_elite_board", {
    staleTime: 30_000, refetchInterval: 60_000,
  });
  if (board.isLoading) return <div className="fp pb-workspace form-elite form-elite-state">Loading FORM ELITE…</div>;
  if (board.error || !board.data) return <div className="fp pb-workspace form-elite form-elite-state"
    role="alert">FORM ELITE is unavailable. Please refresh shortly.</div>;
  return <FormEliteBoard data={board.data} refreshing={board.isFetching}
    onRefresh={() => { void board.refetch(); }} />;
}
